import { EntityModelValues } from "@dune/types";
import { useGetConstructionSites } from "../api/queries/constructionSite.queries";
import { usePropositions } from "../api/queries/propositions.queries";
import { useGetSocieties } from "../api/queries/societies.queries";
import { useGetAssignableUsers } from "../api/queries/tasks.queries";
import { useReceiptsQuery } from "../api/queries/receipt.queries";
import { useGetGFAConstructionSites } from "../api/queries/gfa.queries";

export type EntityOption = {
	id: string;
	label: string;
	value: string;
	subtitle?: string;
};

export const useEntityOptions = (
	entityModel: EntityModelValues,
	search: string,
) => {
	const { data: societies, isLoading: isLoadingSocieties } = useGetSocieties({
		limit: 100,
	});
	const { data: propositions, isLoading: isLoadingPropositions } =
		usePropositions({ limit: 100, search });
	const { data: constructionSites, isLoading: isLoadingConstructionSites } =
		useGetConstructionSites(search, undefined, 100);
	const { data: users, isLoading: isLoadingUsers } = useGetAssignableUsers();

	const { data: receipts, isLoading: isLoadingReceipts } = useReceiptsQuery({
		query: search,
		limit: 100,
	});

	const {
		data: gfaConstructionSites = [],
		isLoading: isLoadingGFAConstructionSites,
	} = useGetGFAConstructionSites("");

	const { options, isLoading } = (() => {
		switch (entityModel) {
			case "SOCIETY":
				return {
					options:
						societies?.map((society) => ({
							id: society.id.toString(),
							label: society.denomination,
							value: society.id.toString(),
							subtitle: society.siren ? `SIREN: ${society.siren}` : undefined,
						})) || [],
					isLoading: isLoadingSocieties,
				};

			case "PROPOSITION":
				return {
					options:
						propositions?.data?.map((proposition) => ({
							id: proposition.id.toString(),
							label: proposition.reference || `Proposition ${proposition.id}`,
							value: proposition.id.toString(),
							subtitle: `Statut: ${proposition.status}`,
						})) || [],
					isLoading: isLoadingPropositions,
				};

			case "CONSTRUCTION_SITE":
				return {
					options:
						constructionSites?.data?.map((site) => ({
							id: site.id.toString(),
							label: `Chantier ${site.id}`,
							value: site.id.toString(),
							subtitle: site.constructionSiteDetails?.address
								? [
										`${site.constructionSiteDetails.address.streetNumber || ""} ${site.constructionSiteDetails.address.streetName || ""}`.trim(),
										site.constructionSiteDetails.address.city || "",
									]
										.filter(Boolean)
										.join(", ")
								: undefined,
						})) || [],
					isLoading: isLoadingConstructionSites,
				};

			case "USER":
				return {
					options:
						users?.map((user) => ({
							id: user.id.toString(),
							label: `${user.firstname} ${user.lastname}`,
							value: user.id.toString(),
							subtitle: user.email,
						})) || [],
					isLoading: isLoadingUsers,
				};
			case "QUITTANCE":
				return {
					options:
						receipts?.data?.map((receipt) => ({
							id: receipt.id.toString(),
							label: receipt.reference,
							value: receipt.id.toString(),
							subtitle: receipt.type,
						})) || [],
					isLoading: isLoadingReceipts,
				};
			case "GFA_CONSTRUCTION_SITE":
				return {
					options:
						gfaConstructionSites?.map((site) => ({
							id: site.id.toString(),
							label: `Projet ${site.id}`,
							value: site.id.toString(),
							subtitle: site.constructionSiteDetails?.address
								? [
										`${site.constructionSiteDetails.address.streetNumber || ""} ${site.constructionSiteDetails.address.streetName || ""}`.trim(),
										site.constructionSiteDetails.address.city || "",
									]
										.filter(Boolean)
										.join(", ")
								: undefined,
						})) || [],
					isLoading: isLoadingGFAConstructionSites,
				};
			default:
				return { options: [], isLoading: false };
		}
	})();

	return { options, isLoading };
};

export const getEntityTypeLabel = (entityModel: EntityModelValues): string => {
	switch (entityModel) {
		case "SOCIETY":
			return "Société";
		case "PROPOSITION":
			return "Proposition / Contrat";
		case "CONSTRUCTION_SITE":
			return "Chantier";
		case "USER":
			return "Utilisateur";
		default:
			return "Entité";
	}
};

export const getEntitySelectionPlaceholder = (
	entityModel: EntityModelValues,
): string => {
	switch (entityModel) {
		case "SOCIETY":
			return "Sélectionnez une société";
		case "PROPOSITION":
			return "Sélectionnez une proposition / un contrat";
		case "CONSTRUCTION_SITE":
			return "Sélectionnez un chantier";
		case "USER":
			return "Sélectionnez un utilisateur";
		default:
			return "Sélectionnez une entité";
	}
};
